// Modo "Borrar objeto" del editor de imagen: un gesto sobre la imagen (un toque, o un
// trazo por encima) y SAM 3 devuelve la máscara de lo que hay ahí.
// Ver ADR-2026-08-13-modo-borrar-objeto-gesto-simple.
import { USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';

/** Punto en píxeles de la imagen REAL (no de lo que se ve en pantalla). */
export interface PuntoImagen {
  x: number;
  y: number;
}

/**
 * En cuántos tramos se parte el trazo. Cada tramo va como un OBJETO distinto.
 *
 * SAM segmenta un objeto por grupo de puntos: si todo el trazo va como un solo objeto y
 * el usuario pasó por encima de dos libros, SAM elige el que mejor encaja y devuelve ése
 * nada más — que es exactamente lo que pasaba. Un objeto por tramo + unión en el backend
 * hace que "lo que marqué" sea todo lo que marcó.
 *
 * 8 es el equilibrio: suficientes tramos para cubrir varios objetos a lo largo del trazo,
 * sin inflar la respuesta (cada objeto vuelve como una máscara PNG).
 */
export const MAX_TRAMOS_TRAZO = 8;

/** Deja como mucho `max` puntos, repartidos parejo a lo largo del trazo. */
export function submuestrear<T>(puntos: T[], max: number = MAX_TRAMOS_TRAZO): T[] {
  if (puntos.length <= max) return puntos;
  const paso = (puntos.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => puntos[Math.round(i * paso)]);
}

/**
 * Pide a SAM 3 la máscara de lo que hay en los puntos marcados. Devuelve un data-URI PNG.
 *
 * `frameUrl` tiene que ser la URL firmada de la imagen **full**, no la del thumbnail: la
 * máscara vuelve del tamaño de la imagen que SAM recibió, y el backend le pasa a Bria la
 * `full/`. Si acá entra un `opt/`, la máscara sale chica y el borrado queda corrido.
 */
export async function requestMascaraBorrado(opts: {
  frameUrl: string;
  puntos: PuntoImagen[];
}): Promise<string> {
  if (!USE_ATLAS_BACKEND) {
    // Modo nuevo: nunca existió en n8n, así que no hay a qué caer.
    throw new Error('El borrado de objetos no está disponible en este entorno.');
  }

  const res = await atlasBackendFetch(
    '/v1/images/mask',
    {
      frame_url: opts.frameUrl,
      // Un toque va como UN objeto. Un trazo va como varios (uno por tramo) y el backend
      // une lo que SAM encuentre en cada uno — ver MAX_TRAMOS_TRAZO.
      points: submuestrear(opts.puntos).map((p, i) => ({
        x: Math.round(p.x),
        y: Math.round(p.y),
        label: 1 as const,
        object_id: opts.puntos.length > 1 ? i : 0,
      })),
      sam3: true,
      modo_utilizado: 'editar_imagen_borrado',
    },
    { signal: AbortSignal.timeout(60_000) },
  );

  const raw = await res.text();
  if (!res.ok) {
    console.error('[borrar-objeto][sam3] HTTP', res.status, '→', raw.slice(0, 600));
    throw new Error('No se pudo detectar el objeto.');
  }

  let json: { mask_png?: string };
  try {
    json = JSON.parse(raw);
  } catch {
    console.error('[borrar-objeto][sam3] respuesta no-JSON:', raw.slice(0, 600));
    throw new Error('El servidor devolvió una respuesta inválida.');
  }
  if (!json.mask_png) throw new Error('No se pudo detectar el objeto.');

  return json.mask_png.startsWith('data:')
    ? json.mask_png
    : `data:image/png;base64,${json.mask_png}`;
}

/**
 * Máscara dibujada a mano a partir del trazo del usuario, en píxeles de la imagen real.
 *
 * Es el plan B de cuando SAM no encuentra un objeto en lo que se marcó: en vez de dejar
 * al usuario sin nada, se borra literalmente lo que pintó. Blanco sobre negro — el
 * backend normaliza igual, pero así la máscara ya sale en el formato canónico.
 */
export function maskDesdeTrazo(
  puntos: PuntoImagen[],
  dims: { width: number; height: number },
  radio: number,
): string | null {
  if (puntos.length === 0) return null;
  const canvas = document.createElement('canvas');
  canvas.width = dims.width;
  canvas.height = dims.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.strokeStyle = 'white';
  ctx.fillStyle = 'white';
  ctx.lineWidth = radio * 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (puntos.length === 1) {
    ctx.beginPath();
    ctx.arc(puntos[0].x, puntos[0].y, radio, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    puntos.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
  }

  return canvas.toDataURL('image/png');
}

function cargarImagen(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo leer la máscara.'));
    img.src = src;
  });
}

/**
 * Une varias máscaras en una sola, a resolución de la imagen real. Es lo que hace que la
 * selección sea ADITIVA: cada gesto nuevo se suma a lo que ya estaba marcado.
 *
 * Corre UNA vez por gesto (no por frame), así que el recorrido píxel a píxel a resolución
 * completa es asumible; lo que no sería asumible es hacerlo mientras el dedo se mueve —
 * por eso el overlay que se dibuja durante el arrastre está cacheado aparte.
 *
 * "Adentro de la máscara" se decide con opacidad Y luminosidad porque las máscaras vienen
 * en dos formatos (binaria opaca, o blanca opaca sobre fondo transparente) y hay que
 * aceptar los dos. La salida es siempre binaria blanco-sobre-negro.
 */
export async function unirMascaras(
  masks: string[],
  dims: { width: number; height: number },
): Promise<string> {
  if (masks.length === 1) return masks[0];

  const out = document.createElement('canvas');
  out.width = dims.width;
  out.height = dims.height;
  const octx = out.getContext('2d');
  if (!octx) return masks[masks.length - 1];
  octx.fillStyle = 'black';
  octx.fillRect(0, 0, out.width, out.height);
  const acumulada = octx.getImageData(0, 0, out.width, out.height);

  const tmp = document.createElement('canvas');
  tmp.width = dims.width;
  tmp.height = dims.height;
  const tctx = tmp.getContext('2d');
  if (!tctx) return masks[masks.length - 1];

  for (const m of masks) {
    const img = await cargarImagen(m);
    tctx.clearRect(0, 0, tmp.width, tmp.height);
    tctx.drawImage(img, 0, 0, tmp.width, tmp.height);
    const d = tctx.getImageData(0, 0, tmp.width, tmp.height).data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] > 127 && (d[i] + d[i + 1] + d[i + 2]) / 3 > 127) {
        acumulada.data[i] = 255;
        acumulada.data[i + 1] = 255;
        acumulada.data[i + 2] = 255;
      }
    }
  }

  octx.putImageData(acumulada, 0, 0);
  return out.toDataURL('image/png');
}

/** El b64 crudo que espera `postImageEdit` en el campo `mask` (sin el prefijo data:). */
export function maskDataUriAB64(dataUri: string): string {
  return dataUri.includes(',') ? dataUri.slice(dataUri.indexOf(',') + 1) : dataUri;
}
